import DocumentViewerModal from "components/DocumentViewer";
import React from "react";
import {
  CivilCaseComplaint,
  CivilCaseResolutionDecisionType,
  FILE_URL,
} from "types";
import { DateView } from "utils/helper";

type Props = {
  compliant: CivilCaseComplaint;
  tab: "appointment" | "forward" | "on_panel";
};

const CivilCaseResolutionTab: React.FC<Props> = ({ compliant, tab }) => {
  const filteredResolutions =
    compliant?.resolutions?.filter((r) =>
      tab === "appointment"
        ? r?.decision_type === "further_review"
        : tab === "on_panel"
          ? r?.decision_type === "on_panel"
          : tab === "forward"
            ? r?.decision_type === "escalate_to_higher_authority" ||
              r?.decision_type === "forward_to_lower_authority"
            : false,
    ) || [];

  if (filteredResolutions.length === 0) {
    return (
      <div className="flex-1 min-w-[350px] animate-fadeIn">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-1 h-6 bg-red-600 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900">
              {tab === "appointment"
                ? "Review Appointments"
                : "Forward Decisions"}
            </h3>
          </div>

          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No resolutions found
            </h3>
            <p className="text-gray-500">
              {tab === "appointment"
                ? "No further review appointments have been scheduled."
                : "No forward decisions have been recorded."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-[350px] animate-fadeIn">
      <div className="bg-white rounded-xl shadow-lg p-6 space-y-8">
        {/* Header */}
        <div className="flex items-center gap-2">
          <div className="w-1 h-6 bg-red-600 rounded-full"></div>
          <h3 className="text-lg font-semibold text-gray-900">
            {tab === "appointment"
              ? "Review Appointments"
              : "Forward Decisions"}
          </h3>
        </div>

        {/* Resolutions Grid */}
        <div className="grid grid-cols-1 gap-6">
          {filteredResolutions.map((resolution, index) => {
            const decisionType =
              resolution?.decision_type &&
              CivilCaseResolutionDecisionType[resolution.decision_type];

            return (
              <div
                key={index}
                className="p-4 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
              >
                {/* Decision Type Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-5 bg-red-600 rounded-full"></div>
                    <h4 className="text-base font-semibold text-gray-900">
                      {decisionType}
                    </h4>
                  </div>
                  <span
                    className={`text-xs font-medium px-2 py-1 rounded-full ${
                      resolution.decision_type === "further_review"
                        ? "bg-amber-100 text-amber-800"
                        : resolution.decision_type ===
                            "escalate_to_higher_authority"
                          ? "bg-purple-100 text-purple-800"
                          : "bg-green-100 text-green-800"
                    }`}
                  >
                    {decisionType}
                  </span>
                </div>

                {/* Decision Summary */}
                <div className="mb-4">
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                    {resolution.decision_summary ||
                      resolution.appointment_reason}
                  </p>
                </div>

                <DocumentViewerModal
                  documents={resolution?.uploaded_documents || []}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.RESOLUTION_PROCESS}`}
                  layout="list"
                  // label="Letter Upload"
                  hideEmptyFile={true}
                />

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex flex-row justify-between border-b border-gray-100 pb-2">
                      <dt className="text-sm font-normal text-gray-500">
                        {decisionType} Date
                      </dt>
                      <dd className="text-sm font-semibold text-gray-900">
                        {DateView(resolution?.appointment_date)}
                      </dd>
                    </div>
                  </div>
                </div>

                {/* Resolver Info */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex flex-row gap-2 items-center">
                    <img
                      src="/person.png"
                      alt="Person"
                      height={40}
                      width={40}
                      className="rounded-full"
                    />
                    <div>
                      <p className="text-sm font-normal text-gray-900">
                        By : {resolution?.resolved_by ?? "-"}
                      </p>
                      <p className="text-xs text-gray-500">Resolver</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CivilCaseResolutionTab;
